import { Link } from 'react-router-dom';
import Seo from '@/components/Seo';
import Starfield from '@/components/Starfield';
import { GrainOverlay } from '@/components/Overlays';
import Footer from '@/components/Footer';


const ideas = [
  {
    idea: 'A website that sells nothing and just shows a rotating photo of a rock.',
    year: '2012',
    what: 'Million Dollar Homepage descendants and "pet rock" style novelty sites repeatedly proved that novelty plus a share button beats utility.',
    lesson: 'A dumb idea with a built-in reason to send it to a friend outperforms a smart idea nobody mentions.',
  },
  {
    idea: 'Let strangers sleep in your spare room.',
    year: '2008',
    what: 'Rejected by most investors as unsafe and unscalable. It was neither.',
    lesson: 'Ideas that sound unsafe are often just unfamiliar. Test the fear, not the vibe.',
  },
  {
    idea: 'A social network where every message disappears.',
    year: '2011',
    what: 'Widely mocked as pointless. Disappearing messages became a default feature everywhere.',
    lesson: 'A feature that sounds like a bug can be the whole product.',
  },
  {
    idea: 'Sell single socks by subscription.',
    year: 'ongoing',
    what: 'Dozens of one-joke subscription boxes launched, most died, a few became real businesses.',
    lesson: 'The joke gets attention. Retention decides whether it was a business.',
  },
  {
    idea: 'An app that only says "no" to you.',
    year: 'ongoing',
    what: 'Joke apps like this regularly top novelty charts for a week, then vanish.',
    lesson: 'Distribution can be free and still leave you with nothing. Plan the second week.',
  },
];

const DumbIdeas = () => (
  <div className="relative min-h-screen bg-background">
    <Starfield />
    <GrainOverlay />

    <Seo
      title="Dumb Ideas That Actually Worked (And Why) | Dumbverse"
      description="Five ideas everyone called stupid, what really happened, and the pattern behind the ones that worked. A practical way to test your own dumb idea."
      path="/dumb-ideas"
      jsonLd={{
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: 'Dumb Ideas That Actually Worked (And Why)',
        author: { '@type': 'Organization', name: 'Dumbverse' },
        publisher: { '@type': 'Organization', name: 'Dumbverse' },
      }}
    />

    <main className="relative z-10 max-w-3xl mx-auto px-6 py-24">
      <p className="font-mono text-xs tracking-[0.3em] uppercase text-muted-foreground mb-4">
        <Link to="/" className="hover:text-foreground transition-colors">Dumbverse</Link> / Dumb ideas
      </p>
      <h1 className="font-bebas text-5xl md:text-7xl text-foreground leading-none">
        DUMB IDEAS THAT ACTUALLY WORKED
      </h1>
      <p className="font-playfair italic text-xl text-muted-foreground mt-4">
        The problem was never that the idea was dumb. The problem was that nobody stress-tested it.
      </p>

      <div className="mt-8 font-mono text-sm text-foreground/80 leading-relaxed space-y-4">
        <p>
          "That's dumb" is not analysis. It is a reflex. Below are five ideas that got that reflex, what actually
          happened to them, and the question that would have told you the answer early.
        </p>
      </div>

      <div className="mt-12 space-y-6">
        {ideas.map(item => (
          <article key={item.idea} className="p-6 rounded-lg border border-border bg-card/30">
            <h2 className="font-bebas text-2xl text-foreground leading-tight">{item.idea}</h2>
            <p className="font-mono text-xs text-muted-foreground mt-1">{item.year}</p>
            <p className="font-mono text-sm text-foreground/80 mt-3 leading-relaxed">{item.what}</p>
            <p className="font-playfair italic text-sm mt-3 text-primary">Lesson: {item.lesson}</p>
          </article>
        ))}
      </div>

      <h2 className="font-bebas text-4xl text-foreground mt-16 mb-4">A four-question test for your own dumb idea</h2>
      <div className="overflow-x-auto">
        <table className="w-full text-left font-mono text-sm border border-border rounded-lg overflow-hidden">
          <thead>
            <tr className="bg-card/50">
              <th className="p-3 font-bebas text-lg text-foreground font-normal">Question</th>
              <th className="p-3 font-bebas text-lg text-foreground font-normal">Who asks it in the council</th>
            </tr>
          </thead>
          <tbody className="text-foreground/80">
            <tr className="border-t border-border"><td className="p-3">What has to be true for this to work?</td><td className="p-3">VERA</td></tr>
            <tr className="border-t border-border"><td className="p-3">Who tried this and why did it die?</td><td className="p-3">MOSS</td></tr>
            <tr className="border-t border-border"><td className="p-3">What is the cheapest shippable version?</td><td className="p-3">PIVOT</td></tr>
            <tr className="border-t border-border"><td className="p-3">Do you actually have capacity for this?</td><td className="p-3">GRIT</td></tr>
          </tbody>
        </table>
      </div>

      <p className="font-mono text-sm text-foreground/80 mt-8 leading-relaxed">
        Those four questions are four of the nine perspectives you get on every post inside Dumbverse. See{' '}
        <Link to="/council" className="text-primary hover:underline">the full council</Link> for the rest.
      </p>

      <div className="mt-12 p-8 rounded-lg border border-primary/40 bg-card/40 text-center">
        <p className="font-bebas text-4xl text-foreground">GOT A DUMB IDEA? GOOD.</p>
        <p className="font-mono text-sm text-muted-foreground mt-2">10,000 founding explorer spots. Then it closes.</p>
        <Link
          to="/"
          className="inline-block mt-6 h-12 px-8 leading-[3rem] rounded bg-primary text-primary-foreground font-bebas text-lg tracking-wide hover:bg-primary/90 transition-colors"
        >
          Join the waitlist
        </Link>
      </div>
    </main>
    <Footer />
  </div>
);

export default DumbIdeas;
